import React from "react";
import { STRENGTH_THRESHOLD } from "../../../../pages/Diagnosticpage/utils/diagnosticHelpers";

/**
 * MemberResultsView Component
 * Admin-side view of a single person's aggregated diagnostic results.
 * Mirrors the client-side ResultsView metric: overall average plus a
 * Strengths / Growth-Opportunities breakdown by action.
 *
 * Props:
 * - aggregatedData: report from useGetTargetFeedbackWithStatsQuery ({ questions, overallStats, actions })
 * - isLoading: loading state for the report fetch
 * - targetName: display name of the person being viewed
 */
export default function MemberResultsView({ aggregatedData, isLoading, targetName }) {
  if (isLoading) {
    return <div className="cd-loading">Loading results...</div>;
  }

  if (!aggregatedData?.overallStats) {
    return (
      <div className="cd-empty-state">
        <p>No diagnostic feedback has been submitted for {targetName || "this employee"} yet.</p>
      </div>
    );
  }

  // Actions arrive pre-scored and sorted by the backend, strongest first.
  const groupedActions = aggregatedData?.actions || [];

  // Split on the rater-relative score, not the raw one: raters rarely go below 4,
  // so a raw cutoff would leave Growth Opportunities permanently empty.
  const strengthActions = groupedActions.filter(
    (a) => a.centeredAverage >= STRENGTH_THRESHOLD
  );
  const growthActions = groupedActions.filter(
    (a) => a.centeredAverage < STRENGTH_THRESHOLD
  );

  const respondents =
    (aggregatedData.overallStats.uniqueRespondents?.self || 0) +
    (aggregatedData.overallStats.uniqueRespondents?.colleague || 0);

  return (
    <div className="cd-results-view">
      {/* Overall stats banner */}
      <div className="cd-results-banner">
        <div className="cd-results-stat">
          <span className="cd-results-stat-value">
            {aggregatedData.overallStats.overallAverage}/5
          </span>
          <span className="cd-results-stat-label">Overall Average</span>
        </div>
        <div className="cd-results-stat">
          <span className="cd-results-stat-value">{groupedActions.length}</span>
          <span className="cd-results-stat-label">Actions Evaluated</span>
        </div>
        <div className="cd-results-stat">
          <span className="cd-results-stat-value">{respondents}</span>
          <span className="cd-results-stat-label">Respondents</span>
        </div>
      </div>

      {/* Strengths / Growth columns */}
      <div className="cd-results-columns">
        <div className="cd-results-column">
          <h4 className="cd-results-column-title cd-results-strength">✓ Strengths</h4>
          {strengthActions.length > 0 ? (
            strengthActions.map((actionData, idx) => (
              <div key={idx} className="cd-results-action cd-results-action-strength">
                <span className="cd-results-action-name">
                  {actionData.action.actionName}
                </span>
                <span className="cd-results-action-score">
                  {actionData.average.toFixed(1)}/5
                </span>
              </div>
            ))
          ) : (
            <p className="cd-answer-empty">Not enough feedback to compare actions yet.</p>
          )}
        </div>

        <div className="cd-results-column">
          <h4 className="cd-results-column-title cd-results-growth">↗ Growth Opportunities</h4>
          {growthActions.length > 0 ? (
            growthActions.map((actionData, idx) => (
              <div key={idx} className="cd-results-action cd-results-action-growth">
                <div className="cd-results-action-info">
                  <span className="cd-results-action-name">
                    {actionData.action.actionName}
                  </span>
                  {/* The score can still look high on its own; this column is
                      relative, so say what put the action here. */}
                  <span className="cd-results-action-context">
                    Rated below their average
                  </span>
                </div>
                <span className="cd-results-action-score">
                  {actionData.average.toFixed(1)}/5
                </span>
              </div>
            ))
          ) : (
            <p className="cd-answer-empty">Not enough feedback to compare actions yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
